/**
 *
 * App.js
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 *
 */

import React, { useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import validator from 'validator';

import { connect } from 'react-redux';
import { compose } from '@reduxjs/toolkit';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';

import EkycPage from 'containers/EkycPage/Loadable';
import EkycRedirectPage from 'containers/EkycRedirectPage/Loadable';
import Header from 'components/Header';
import Footer from 'components/Footer';
import { routeHandler, getUrlParam, setNavigate } from '../../helpers/route';
import saga from './saga';
import reducer from './reducer';
import { updateKycId, fetchPublicKeyRequest } from '../EkycPage/actions';
import {
  onThemeChange,
  updateAuthToken,
  initialLoadFailure,
  tokenDetailsRequest
} from './actions';

import Status from '../../components/Status';
import LanguageSelectModal from '../../components/LanguageSelectModal';
import VidModal from '../../components/VidModal';

import { THEME_MODE } from './constants';
import { setMessagesAsPerTheme } from '../../utils/common';

import style from './style.module.scss';

export function App(props) {
  useInjectReducer({ key: 'app', reducer });
  useInjectSaga({ key: 'app', saga });

  const location = useLocation();
  const navigate = useNavigate();
  const mounted = useRef();

  useEffect(() => {
    setNavigate(navigate);
  }, [navigate]);

  useMemo(() => {
    const mode = getUrlParam('mode', null);
    let themeMode = props.theme;
    themeMode = THEME_MODE[mode];
    if (!themeMode) themeMode = THEME_MODE['1'];
    document.documentElement.setAttribute('data-theme-mode', themeMode);
    setMessagesAsPerTheme(themeMode);
    props.onThemeChange(themeMode);
  }, []);

  useEffect(() => {
    if (!mounted.current) {
      if (location.pathname === '/ekyc_redirect') {
        routeHandler('/ekyc_redirect');
      } else {
        routeHandler('/');
      }
      mounted.current = true;
    }

    // Component Did Update
    if (location.pathname !== '/ekyc_redirect') {
      const kycId = getUrlParam('kyc_id', null);
      if (!kycId || !validator.isUUID(kycId)) {
        props.oninitialLoadFailure();
      } else {
        props.onTokenDetailsRequest();
        props.onUpdateKycId(kycId);
        props.fetchPublicKey();
      }
    }


    // Handle Browser Back Action
    const isSafari = !!navigator.userAgent.match(/Version\/[\d\.]+.*Safari/);
    if (isSafari) {
      history.pushState(null, null, document.URL);
      window.onpopstate = e => {
        history.pushState(null, null, document.URL);
        window.location.reload();
      };
    } else {
      window.onpopstate = e => {
        window.location.reload();
      };
    }
  }, []);

  const showNotifications = () =>
    props.app.loading ||
    props.app.authLoading ||
    props.ekyc.loading ||
    props.app.serverError ||
    props.ekyc.serverError ||
    props.app.linkError ||
    props.app.tokenExpiry;

  return (
    <div className={`appContainer ${style.appContainer}`}>
      {location.pathname !== '/ekyc_redirect' && <Header theme={props.theme} />}
      <div className={`contentContainer ${style.contentContainer}`}>
        {showNotifications() != true && (
          <Routes>
            <Route path="/" element={<EkycPage />} />
            <Route path="/ekyc_redirect" element={<EkycRedirectPage />} />
          </Routes>
        )}
      </div>
      <Status />
      {location.pathname !== '/ekyc_redirect' && <Footer
        theme={props.theme}
        display={showNotifications() != true}
      />}
      <LanguageSelectModal />
      <VidModal />
    </div>
  );
}

App.propTypes = {
  fetchPublicKey: PropTypes.func,
  onUpdateAuthToken: PropTypes.func,
  onUpdateKycId: PropTypes.func,
  oninitialLoadFailure: PropTypes.func,
  onTokenDetailsRequest: PropTypes.func,
};

const mapStateToProps = state => ({
  app: state.app,
  ekyc: state.ekyc,
  theme: state.app.theme,
});

function mapDispatchToProps(dispatch) {
  return {
    fetchPublicKey: () => dispatch(fetchPublicKeyRequest()),
    onUpdateAuthToken: token => dispatch(updateAuthToken(token)),
    onUpdateKycId: kycId => dispatch(updateKycId(kycId)),
    oninitialLoadFailure: () => dispatch(initialLoadFailure()),
    onThemeChange: theme => dispatch(onThemeChange(theme)),
    onTokenDetailsRequest: () => dispatch(tokenDetailsRequest()),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(withConnect)(App);
